import * as moment from 'moment'
const momentDurationFormatSetup = require('moment-duration-format')
momentDurationFormatSetup(moment)
import {ITime, TimeUnit} from '@eventops/athlinks-results-model'
import {
  DisplayData,
  isPlural,
  LabelPlurality,
  LabelPluralityMap,
  LabelVerbosity,
  UnitMap,
  BasicConversionArgs,
  Precision,
  makePrecise
} from '.'
import {
  capitalize as loCap,
  padStart,
  isFunction
} from 'lodash'
import {TimeTemplate, DefaultTimeTemplates} from './timeCommon'
import {UnitLog} from '..'

function buildShortLabelMap(unit: TimeUnit): LabelPluralityMap {
  return {
    singular: unit,
    plural: unit
  }
}

function buildTimeLabelMap(): UnitMap {
  let map = {}
  map[TimeUnit.MILLI_SECONDS] = () => ({
    'short': buildShortLabelMap(TimeUnit.MILLI_SECONDS),
    'standard': {
      singular: 'millisecond',
      plural: 'milliseconds'
    }
  })
  map[TimeUnit.SECONDS] = () => ({
    'short': buildShortLabelMap(TimeUnit.SECONDS),
    'standard': {
      singular: 'second',
      plural: 'seconds'
    }
  })
  map[TimeUnit.MINUTES] = () => ({
    'short': buildShortLabelMap(TimeUnit.MINUTES),
    'standard': {
      singular: 'minute',
      plural: 'minutes'
    }
  })
  map[TimeUnit.HOURS] = () => ({
    'short': buildShortLabelMap(TimeUnit.HOURS),
    'standard': {
      singular: 'hour',
      plural: 'hours'
    }
  })
  // The default case
  map['undefined'] = () => ({
    'short': buildShortLabelMap(TimeUnit.MILLI_SECONDS),
    'standard': {
      singular: 'millisecond',
      plural: 'milliseconds'
    }
  })
  return map
}

const timeLabelLongMap = buildTimeLabelMap()

export function findLabel(
  magnitude: number,
  unit: TimeUnit,
  verbosity: LabelVerbosity
): string {
  const plurality =
    (isPlural(magnitude)) ? LabelPlurality.PLURAL : LabelPlurality.SINGULAR
  return timeLabelLongMap[unit]()[verbosity][plurality]
}

export interface TimeConversionArgs extends BasicConversionArgs {
  template?: TimeTemplate,
  roundUpSeconds?: boolean
}

const timeDefaultTemplateMap = {
  ['ms']: () => DefaultTimeTemplates.Millis,
  ['s']: () => DefaultTimeTemplates.MinutesSeconds,
  ['m']: () => DefaultTimeTemplates.HoursMinutesSeconds,
  ['h']: () => DefaultTimeTemplates.HoursMinutesSeconds
}

interface RawTimeConverter {
  (ms: number, precision?: Precision, unitType?: TimeUnit): number
}

// This is here just for type completeness,
// it should never do much in terms of precision
const msToMs: RawTimeConverter =
 (ms: number, precision?: Precision, unitType?: TimeUnit): number =>
    makePrecise(ms, precision, unitType)

const msToSeconds: RawTimeConverter =
 (ms: number, precision?: Precision, unitType?: TimeUnit): number =>
    makePrecise(ms / 1000, precision, unitType)

const msToMinutes: RawTimeConverter =
 (ms: number, precision?: Precision, unitType?: TimeUnit): number =>
    makePrecise(msToSeconds(ms, precision, unitType) / 60, precision, unitType)

const msToHours: RawTimeConverter =
 (ms: number, precision?: Precision, unitType?: TimeUnit): number =>
    makePrecise(msToMinutes(ms, precision, unitType) / 60, precision, unitType)

const rawTimeConverter = {
  ['ms']: msToMs,
  ['s']: msToSeconds,
  ['m']: msToMinutes,
  ['h']: msToHours
}

/**
 * This is used in pace calculations only
 *
 * @param time TimeUnit in normalized format
 * @param precision Precision
 */
export function convertTimeValue(
  time: ITime,
  precision?: Precision
): number {
  const {
    timeInMillis,
    timeUnit
  } = time
  return rawTimeConverter[timeUnit](timeInMillis, precision, timeUnit)
}

const defaultTimeTemplateLabelMap = {
  [DefaultTimeTemplates.Millis]: () => TimeUnit.MILLI_SECONDS,
  [DefaultTimeTemplates.SecondsMillis]: () => TimeUnit.SECONDS,
  [DefaultTimeTemplates.MinutesSeconds]: () => TimeUnit.MINUTES,
  [DefaultTimeTemplates.HoursMinutesSeconds]: () => TimeUnit.HOURS
}

export function mapDefaultTimeTemplateLabel(
  timeUnit: TimeUnit,
  template?: TimeTemplate
): TimeUnit {
  const temp = isFunction(template) ? template() : template
  const mapper = defaultTimeTemplateLabelMap[temp]
  return mapper ? mapper() : timeUnit
}

export function roundUpToTheNearestSecond(timeInMillis: number): number {
  const delta = timeInMillis % 1000
  return delta ? timeInMillis - delta + 1000 : timeInMillis
}

export function convertTime(
  time: ITime,
  args: TimeConversionArgs = {}
): DisplayData<string> {
  const {
    timeInMillis: rawMillis,
    timeUnit
  } = time
  const {
    template,
    verbosity = LabelVerbosity.SHORT,
    capitalize = false,
    roundUpSeconds = true
  } = args
  let actualTemplate = undefined
  if (rawMillis < 0) {
    UnitLog.warn(`Negative time value found (${rawMillis}), returning empty.`)
    return {
      value: '',
      label: ''
    }
  }
  const timeInMillis = roundUpSeconds ?
    roundUpToTheNearestSecond(rawMillis) : rawMillis
  try {
    const millis = moment.duration(timeInMillis, 'milliseconds')
    actualTemplate =
      (template) ? template : timeDefaultTemplateMap[timeUnit]()
    // seconds are a special case
    const value = padStart(millis.format(actualTemplate, {}), 4, '0:0')
    const timeUnitForLabel = mapDefaultTimeTemplateLabel(timeUnit, template)
    const label = findLabel(timeInMillis, timeUnitForLabel, verbosity)
    return {
      value,
      label: (capitalize) ? loCap(label) : label
    }
  } catch {
    UnitLog.warn(
      `Could not convert time ${timeInMillis} to unit type '${timeUnit}'` +
      `${actualTemplate ? ` with format '${actualTemplate}'.` : '.'}`
    )
    return {
      value: timeInMillis.toString(),
      label: TimeUnit.MILLI_SECONDS
    }
  }
}